/**
 * Admin Dashboard — "Today Inside Chambers"
 * Single-scroll operational surface. Answers: "What requires attention right now?"
 */
import { useEffect, useMemo } from 'react';
import { isOpenMatterStatus } from '@/lib/matterStatus';
import { todayKey as getTodayKey } from '@/lib/diaryStore';
import { Link, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  CalendarBlank as CalendarIcon,
  Users,
  TrendUp as TrendingUp,
  CaretRight as ChevronRight,
  CurrencyGbp,
  Gavel,
  WarningCircle,
  CheckCircle,
  ArrowRight,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useApiBarristersStore } from '@/lib/barristersApi';
import { fetchActivityTotals } from '@/lib/activitiesApi';
import {
  fetchOperationalAlerts,
  fetchBarristerWorkload,
  type OperationalAlert,
  type BarristerWorkloadItem,
} from '@/lib/reportsApi';
import { fetchDashboardMetrics } from '@/lib/meApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatCard } from '@/components/shared/StatCard';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { ActivityLogPanel } from '@/components/shared/ActivityLogPanel';
import { AwaitingResponseTray } from '@/components/shared/AwaitingResponseTray';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

// ─── Helpers ─────────────────────────────────────────────────

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysUntil(isoDate: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate + 'T00:00:00'); target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000);
}

function countdownLabel(days: number): string {
  if (days < 0) return `${Math.abs(days)}d ago`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days}d`;
}

function fmtGbp(n: number): string {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`;
  return `£${n.toLocaleString('en-GB')}`;
}

const WORKLOAD_DOT: Record<BarristerWorkloadItem['workloadStatus'], string> = {
  overloaded: 'bg-red-500',
  high:       'bg-amber-400',
  normal:     'bg-emerald-500',
  light:      'bg-emerald-300',
};

const WORKLOAD_LABEL: Record<BarristerWorkloadItem['workloadStatus'], string> = {
  overloaded: 'Overloaded',
  high:       'High',
  normal:     'Normal',
  light:      'Light',
};

// ─────────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const chambers  = useAuthStore((s) => s.chambers);
  const navigate  = useNavigate();
  const todayKey  = useMemo(() => getTodayKey(), []);
  const weekAhead = useMemo(() => dateKey(new Date(Date.now() + 7 * 86_400_000)), []);
  const now       = new Date();

  // ── Stores ──────────────────────────────────────────────────
  const matters            = useMattersStore((s) => s.matters);
  const fetchMatters       = useMattersStore((s) => s.fetchMatters);
  const allHearings        = useHearingsStore((s) => s.hearings);
  const fetchHearings      = useHearingsStore((s) => s.fetchHearings);
  const barristers         = useApiBarristersStore((s) => s.barristers);
  const fetchBarristersApi = useApiBarristersStore((s) => s.fetch);

  useEffect(() => {
    fetchMatters();
    fetchHearings();
    fetchBarristersApi();
  }, [fetchMatters, fetchHearings, fetchBarristersApi]);

  // ── React Query — operational intelligence ──────────────────
  const alertsQ = useQuery({
    queryKey: ['operational-alerts'],
    queryFn: fetchOperationalAlerts,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const workloadQ = useQuery({
    queryKey: ['barrister-workload'],
    queryFn: fetchBarristerWorkload,
    staleTime: 120_000,
  });

  const mtdStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const totalsQ = useQuery({
    queryKey: ['activity-totals-mtd'],
    queryFn: () => fetchActivityTotals({ dateFrom: mtdStart }),
    staleTime: 300_000,
  });

  // ── Computed ────────────────────────────────────────────────
  const activeMatters = matters.filter((m) => isOpenMatterStatus(m.status)).length;

  const todayHearings = useMemo(
    () => allHearings
      .filter((h) => h.date === todayKey && h.status !== 'completed' && h.status !== 'vacated')
      .sort((a, b) => a.time.localeCompare(b.time)),
    [allHearings, todayKey],
  );

  const thisWeekHearings = useMemo(
    () => allHearings
      .filter((h) => h.date > todayKey && h.date <= weekAhead && h.status !== 'completed' && h.status !== 'vacated')
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)),
    [allHearings, todayKey, weekAhead],
  );

  // Chambers-wide win rate is a backend-owned fact (canonical computeWinRate; null below a
  // 3-matter sample). An admin has no barrister profile, so /me/dashboard-metrics returns
  // chambers-wide figures. The UI renders — it never recomputes from the matter list.
  const metricsQ = useQuery({ queryKey: ['dashboard-metrics'], queryFn: fetchDashboardMetrics });
  const winRate = metricsQ.data?.practiceHealth.winRate ?? null;
  const decidedMatters = metricsQ.data?.practiceHealth.closedMatters ?? 0;

  if (alertsQ.isError || workloadQ.isError || totalsQ.isError || metricsQ.isError) {
    return <QueryErrorState what="dashboard" />;
  }

  // Alerts: critical first, then warnings
  const alerts = alertsQ.data ?? [];
  const criticals = alerts.filter((a) => a.level === 'critical');
  const warnings  = alerts.filter((a) => a.level === 'warning');

  return (
    <div className="space-y-6">

      {/* ═════════════════════════════════════════════════════
          OPERATIONAL ALERTS — surfaces when anything needs attention.
          Empty state is quiet and reassuring, not hidden.
         ═════════════════════════════════════════════════════ */}
      {alertsQ.isSuccess && (
        alerts.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5 text-sm">
            <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" weight="fill" />
            <span className="font-medium text-emerald-800 dark:text-emerald-300">No operational alerts</span>
            <span className="text-emerald-600 dark:text-emerald-400">· {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        ) : (
          <div className={`rounded-lg border px-4 py-3 ${criticals.length > 0 ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10' : 'border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'}`}>
            <div className="mb-2 flex items-center gap-2">
              <WarningCircle className={`h-4 w-4 ${criticals.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} weight="fill" />
              <span className={`text-sm font-semibold ${criticals.length > 0 ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>
                {alerts.length} operational alert{alerts.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[...criticals, ...warnings].map((a) => (
                <AlertChip key={a.key} alert={a} navigate={navigate} />
              ))}
            </div>
          </div>
        )
      )}

      {/* ═════════════════════════════════════════════════════
          HEADER
         ═════════════════════════════════════════════════════ */}
      <PageHeader
        title="Dashboard"
        subtitle={`${chambers?.name ?? 'Chambers'} · ${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}
        actions={
          <>
            <Button variant="outline" size="md" onClick={() => navigate('/admin/reports')}>Reports</Button>
            <Button variant="primary" size="md" leftIcon={<Briefcase className="h-4 w-4" />} onClick={() => navigate('/admin/matters/new')}>
              New matter
            </Button>
          </>
        }
      />

      {/* Live brief offers */}
      <AwaitingResponseTray basePath="/admin" />

      {/* ═════════════════════════════════════════════════════
          TODAY IN COURT
         ═════════════════════════════════════════════════════ */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-50">
                <Gavel className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />
              </div>
              <div>
                <CardTitle className="text-[15px]">Today in court</CardTitle>
                <CardDescription className="text-xs">
                  {todayHearings.length === 0 ? 'No hearings today' : `${todayHearings.length} hearing${todayHearings.length > 1 ? 's' : ''} listed`}
                </CardDescription>
              </div>
            </div>
            <Link to="/admin/hearings" className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
              All hearings <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </CardHeader>
        {todayHearings.length === 0 ? (
          <CardContent className="pb-5 pt-0 text-center">
            <CalendarIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No hearings scheduled for today</p>
          </CardContent>
        ) : (
          <CardContent className="px-0 pb-0">
            <div className="overflow-x-auto">
              {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Matter</th>
                    <th>Court</th>
                    <th>Barrister</th>
                    <th>Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {todayHearings.map((h) => (
                    <tr key={h.id} className="cursor-pointer border-l-2 border-l-brand-400 bg-brand-50/20" onClick={() => navigate(`/admin/hearings/${h.id}`)}>
                      <td className="text-xs font-semibold text-brand-700 dark:text-brand-300">{h.time}</td>
                      <td className="font-medium text-foreground">{h.matterName}</td>
                      <td className="text-muted-foreground">{h.court}</td>
                      <td className="text-foreground">{h.leadBarrister}</td>
                      <td><Badge>{h.type}</Badge></td>
                      <td>
                        <Badge variant={h.status === 'confirmed' ? 'success' : 'warning'}>
                          {h.status === 'confirmed' ? 'Confirmed' : 'Scheduled'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* ═════════════════════════════════════════════════════
          KPI STATS ROW
         ═════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          to="/admin/matters"
          icon={<Briefcase className="h-4 w-4" />}
          label="Active matters"
          value={activeMatters}
          hint="open + listed"
        />
        <StatCard
          to="/admin/hearings"
          icon={<CalendarIcon className="h-4 w-4" />}
          label="Hearings this week"
          value={todayHearings.length + thisWeekHearings.length}
          hint={`${todayHearings.length} today`}
        />
        <StatCard
          to="/admin/fees"
          icon={<CurrencyGbp className="h-4 w-4" />}
          label="Billed (MTD)"
          value={totalsQ.data ? fmtGbp(totalsQ.data.billableAmount) : '…'}
          hint="billable entries"
        />
        <StatCard
          to="/admin/barristers"
          icon={<TrendingUp className="h-4 w-4" />}
          label="Win rate"
          value={winRate === null ? '—' : `${winRate}%`}
          hint={`${decidedMatters} decided matters`}
        />
      </div>

      {/* ═════════════════════════════════════════════════════
          2-COLUMN: Upcoming hearings + Barrister workload
         ═════════════════════════════════════════════════════ */}
      {/* `[&>*]:min-w-0` — a grid item's `min-width` is `auto`, so the single column below `lg`
          was sized by the widest card's minimum content rather than by the screen, and the cards
          hung off the right of a 320px phone. */}
      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">

        {/* Left: Upcoming this week */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Upcoming this week</CardTitle>
                <CardDescription>{thisWeekHearings.length} hearing{thisWeekHearings.length !== 1 ? 's' : ''} · next 7 days</CardDescription>
              </div>
              <Link to="/admin/hearings" className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                View all <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </CardHeader>
          {thisWeekHearings.length === 0 ? (
            <CardContent className="pb-6 pt-0 text-center">
              <p className="text-sm text-muted-foreground">No further hearings this week</p>
            </CardContent>
          ) : (
            <CardContent className="px-0 pb-0">
              {thisWeekHearings.slice(0, 8).map((h) => {
                const days = daysUntil(h.date);
                const urgency = days <= 1 ? 'urgent' : days <= 3 ? 'attention' : null;
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => navigate(`/admin/hearings/${h.id}`)}
                    className={`flex w-full items-center gap-4 border-l-2 px-5 py-3 text-left transition hover:bg-muted/40 ${
                      urgency === 'urgent'    ? 'border-l-red-500 bg-red-50/30 dark:bg-red-500/10'    :
                      urgency === 'attention' ? 'border-l-amber-400 bg-amber-50/20 dark:bg-amber-500/8' :
                      'border-l-transparent'
                    }`}
                  >
                    <div className="w-20 shrink-0">
                      <div className={`text-[10px] font-semibold uppercase tracking-wider ${urgency === 'urgent' ? 'text-red-700 dark:text-red-300' : urgency === 'attention' ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
                        {countdownLabel(days)}
                      </div>
                      <div className="text-xs font-semibold text-foreground">{h.date.slice(5).replace('-', ' ')}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">{h.matterName}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{h.court} · {h.time} · {h.leadBarrister}</div>
                    </div>
                    <Badge>{h.type}</Badge>
                  </button>
                );
              })}
            </CardContent>
          )}
        </Card>

        {/* Right: Barrister workload */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Barrister workload</CardTitle>
                <CardDescription>Active matters · hearing load</CardDescription>
              </div>
              <Link to="/admin/barristers" className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                View <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {workloadQ.isLoading ? (
              <div className="px-6 py-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (workloadQ.data ?? []).length === 0 ? (
              <div className="px-6 py-6 text-center text-sm text-muted-foreground">No barristers found</div>
            ) : (
              <div className="divide-y divide-border">
                {(workloadQ.data ?? []).slice(0, 6).map((b) => (
                  <Link
                    key={b.barristerId}
                    to={`/admin/barristers/${b.barristerId}`}
                    className="flex items-center gap-3 px-5 py-2.5 transition hover:bg-muted/40"
                  >
                    <Avatar
                      fallback={`${b.firstName[0]}${b.lastName[0]}`}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {b.firstName} {b.lastName}{b.memberType === 'silk' ? ' KC' : ''}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {b.activeMatters} matter{b.activeMatters !== 1 ? 's' : ''} · {b.hearings7d} hearing{b.hearings7d !== 1 ? 's' : ''} this week
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className={`inline-block h-2 w-2 rounded-full ${WORKLOAD_DOT[b.workloadStatus]}`} />
                      <span className="text-[10px] font-medium text-muted-foreground">{WORKLOAD_LABEL[b.workloadStatus]}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ═════════════════════════════════════════════════════
          LIVE ACTIVITY FEED + QUICK ACTIONS
         ═════════════════════════════════════════════════════ */}
      {/* `[&>*]:min-w-0` — a grid item's `min-width` is `auto`, so the single column below `lg`
          was sized by the widest card's minimum content rather than by the screen, and the cards
          hung off the right of a 320px phone. */}
      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ActivityLogPanel
            title="Chambers activity"
            showSummary={true}
            limit={15}
            defaultFilter="all"
          />
        </div>

        {/* Quick navigation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Quick actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pb-4">
            {[
              { label: 'Matters',      hint: `${activeMatters} active`,                      to: '/admin/matters',      icon: Briefcase    },
              { label: 'Hearings',     hint: `${todayHearings.length + thisWeekHearings.length} this week`,    to: '/admin/hearings',     icon: CalendarIcon },
              { label: 'Barristers',   hint: `${barristers.length} in chambers`,               to: '/admin/barristers',   icon: Users        },
              { label: 'Reports',      hint: 'Operational intelligence',                      to: '/admin/reports',      icon: TrendingUp   },
            ].map(({ label, hint, to, icon: Icon }) => (
              <button
                key={to}
                type="button"
                onClick={() => navigate(to)}
                className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-left transition hover:bg-muted/50 hover:border-brand-200"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-50">
                  <Icon className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">{label}</div>
                  <div className="text-xs text-muted-foreground">{hint}</div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Alert chip ───────────────────────────────────────────────

function AlertChip({ alert, navigate }: { alert: OperationalAlert; navigate: (to: string) => void }) {
  const ALERT_NAV: Record<string, string> = {
    hearings_imminent_no_prep:   '/admin/hearings',
    overdue_fee_notes:           '/admin/fees',
    unassigned_matters:          '/admin/matters',
    pending_conferences:         '/admin/calendar',
    stalled_matters:             '/admin/matters',
    pending_allocations:         '/admin/hearings',
  };
  const to = ALERT_NAV[alert.key] ?? '/admin/reports';
  const isCritical = alert.level === 'critical';

  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition hover:opacity-80 ${
        isCritical
          ? 'border-red-300 dark:border-red-500/40 bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300'
          : 'border-amber-300 dark:border-amber-500/40 bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'
      }`}
    >
      {isCritical ? (
        <WarningCircle className="h-3 w-3" weight="fill" />
      ) : (
        <WarningCircle className="h-3 w-3" />
      )}
      {alert.message}
      {alert.count > 0 && (
        <span className={`ml-0.5 rounded-full px-1 py-0.5 text-[9px] font-bold ${isCritical ? 'bg-red-200 text-red-900 dark:text-red-200' : 'bg-amber-200 text-amber-900 dark:text-amber-200'}`}>
          {alert.count}
        </span>
      )}
    </button>
  );
}
